// sign_in_view — the console's decisions about a « se connecter avec… » secret (OAuth2 authorization
// code). Pure, so each one is tested without a browser.

import type { SignInStatus } from "../api";

/** The address the owner registers at the service. It MUST equal what the server sends as
 *  `redirect_uri` (platform-service/lib/oauth_pending.ts callbackUri) — the service compares them
 *  character for character, and a mismatch is a refusal at the service, not here. */
export const signInCallbackUri = (origin: string, slug: string, name: string): string => `${origin}/oauth/callback/${slug}/${name}`;

/** The name of the channel the callback page announces on. Not `window.opener`: a service whose
 *  sign-in page sets Cross-Origin-Opener-Policy cuts the window from its opener, and a reference
 *  cut once stays cut after the window comes back to us. A BroadcastChannel only needs the origin. */
export const SIGN_IN_CHANNEL = "perimetr-sign-in";

export function signInLabel(s: SignInStatus["status"]): { text: string; tone: "ok" | "warn" } {
  if (s === "connected") return { text: "Connected", tone: "ok" };
  if (s === "reconnect") return { text: "Connect again", tone: "warn" };
  return { text: "Not connected", tone: "warn" };
}

/** The button's word: a first connection, or one that replaces the account connected now — through the
 *  service named when it is PERIMETR's app there (platform-oauth). */
export const connectWord = (s: SignInStatus["status"], provider: string | null = null): string => {
  const verb = s === null ? "Connect" : "Reconnect";
  return provider ? `${verb} with ${providerTitle(provider)}` : verb;
};

/** How a provider is named to a person. An id this console does not know is shown as it is. */
export function providerTitle(id: string): string {
  return id === "google" ? "Google" : id;
}

/**
 * One finish per answer. React's StrictMode runs an effect twice in development, and `state` is
 * single-use on the server: a second POST of the same answer would turn a success into « unknown or
 * expired ». The SAME promise is handed back for the same answer.
 */
const finishing = new Map<string, Promise<unknown>>();
export function finishOnce<T>(key: string, run: () => Promise<T>): Promise<T> {
  let p = finishing.get(key) as Promise<T> | undefined;
  if (!p) { p = run(); finishing.set(key, p); }
  return p;
}
